import type { INotification } from "../../types/notifications";

/**
 * Минимум, который нужен предикату. Берётся структурно, а не целым
 * `INotification`, чтобы им могли пользоваться и панель, и мобильный список, и
 * тест, не собирая уведомление целиком.
 */
export type LinkBearingNotification = Pick<INotification, "linkUrl"> & {
  data?: Record<string, unknown> | null;
};

/**
 * Куда ведёт уведомление — внутренний путь фронта либо `null`.
 *
 * ПОЧЕМУ ЭТО ВООБЩЕ ПОНАДОБИЛОСЬ (AD-150). Ссылку в системе кладут тринадцать
 * мест бэкенда, и все кладут её ВНУТРЬ `data` (`data.linkUrl`) — там же её
 * читают почтовый и push-транспорты (`email.service.ts`,
 * `mobile-push.service.ts`). Колонки `linkUrl` у сущности `Notification` нет
 * вовсе. Фронт же читал `notification.linkUrl` ВЕРХНИМ уровнем, а
 * `fetchNotifications` кладёт ответ сервера в стор без преобразования
 * (`notificationsSlice.ts`, `return response.data`). Значит поле было
 * `undefined` всегда, и по клику не открывалось ни одно уведомление в
 * приложении — не только про регистрацию на событие.
 *
 * ИСТОЧНИК ИСТИНЫ — `data.linkUrl`: за ним тринадцать писателей и два
 * работающих потребителя против нуля у верхнего уровня. Верхний уровень
 * оставлен запасным входом, а не убран: пустой поиск не доказывает, что
 * производителя нет (PIV п.7), а цена запасной ветки — одна строка.
 *
 * ПРОВЕРКА ТИПА В РАНТАЙМЕ ОБЯЗАТЕЛЬНА: `data` — это `jsonb`, туда физически
 * может лечь что угодно, и нестроковое значение иначе ушло бы в переход как
 * `[object Object]`. Тот же рантайм-контроль по той же причине уже стоит на
 * бэкенде в обоих транспортах.
 *
 * ТОЛЬКО ВНУТРЕННИЕ ПУТИ. Значение приходит с сервера, то есть из-за границы
 * доверия, и уходит в навигацию. Поэтому пропускается лишь путь, начинающийся
 * с одного `/`: абсолютный адрес и протокол-относительный `//host` — это выход
 * наружу, и он отвергается (fail-closed), а не открывается.
 */
export function resolveNotificationLink(
  notification: LinkBearingNotification,
): string | null {
  const fromData: unknown = notification.data?.["linkUrl"];
  const candidate: unknown =
    typeof fromData === "string" ? fromData : notification.linkUrl;

  if (typeof candidate !== "string") {
    return null;
  }

  const path = candidate.trim();

  if (!path.startsWith("/") || path.startsWith("//")) {
    return null;
  }

  return path;
}
